<template>
  <div>
    <!-- Mismo estilo que las tablas de la plataforma (GenericBTable), que es lo que usa
         CapacityTable para las Capacidades de Duoc -->
    <div class="selection-table-container">
      <table class="table table-sm selection-table mb-0">
        <thead>
          <tr>
            <th class="selection-column">
              <div class="custom-control custom-checkbox">
                <input
                  id="matter-realization-scopes-select-all"
                  type="checkbox"
                  class="custom-control-input"
                  :checked="allSelected"
                  :disabled="availableScopes.length == 0 || saving"
                  @change="selectAll($event.target.checked)"
                />
                <label
                  class="custom-control-label"
                  for="matter-realization-scopes-select-all"
                  :title="
                    allSelected
                      ? $t('reusable.common.deselectAll')
                      : $t('reusable.common.selectAll')
                  "
                ></label>
              </div>
            </th>
            <th>{{ entityNamePlural }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="availableScopes.length > 0">
            <tr
              v-for="realization_scope in availableScopes"
              :key="realization_scope.id"
            >
              <td class="selection-column">
                <div class="custom-control custom-checkbox">
                  <input
                    :id="`matter-realization-scope-${realization_scope.id}`"
                    v-model="selected_ids"
                    type="checkbox"
                    class="custom-control-input"
                    :value="realization_scope.id"
                    :disabled="saving"
                  />
                  <label
                    class="custom-control-label"
                    :for="`matter-realization-scope-${realization_scope.id}`"
                  ></label>
                </div>
              </td>
              <td class="text-left">
                <label
                  :for="`matter-realization-scope-${realization_scope.id}`"
                  class="mb-0 scope-name"
                  >{{ realization_scope.name }}</label
                >
                <div
                  v-if="realization_scope.description"
                  class="scope-description"
                >
                  {{ realization_scope.description }}
                </div>
              </td>
            </tr>
          </template>
          <tr v-else>
            <td colspan="2" class="text-center">
              {{
                $t("reusable.common.noDataContextToShow", {
                  entity: entityNamePlural,
                })
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="d-flex mt-3">
      <button
        type="button"
        class="btn btn-secondary btn-sm btn-cancel"
        @click="emit('close')"
      >
        {{ $t("reusable.common.cancel") }}
      </button>
      <!-- Deshabilitado mientras se guarda: varios clics enviaban varias peticiones.
           Si la petición falla, PATCH_EGRESS_PROFILE_MATTER no rechaza (api.js ya muestra
           el error), así que queda deshabilitado hasta cerrar con Cancelar -->

      <button
        type="button"
        class="btn btn-secondary btn-sm mr-3"
        :disabled="saving"
        @click="save"
      >
        <span
          v-if="saving"
          class="spinner-border spinner-border-sm mr-1"
          aria-hidden="true"
        ></span>
        {{ saving ? $t("reusable.common.saving") : $t("reusable.common.save") }}
      </button>
    </div>
  </div>
</template>

<script setup>
// eslint-plugin-vue 6.x (anterior a <script setup>) no ve que estos bindings
// se usan en el template. Quitar el disable cuando se actualice el tooling.
/* eslint-disable no-unused-vars */
// Selección de los Ámbitos de realización de una asignatura del perfil (Matriz 2 y
// programa). Solo se ofrecen los vinculados a las competencias seleccionadas, igual que
// las Capacidades de Duoc en CapacityTable.
import { computed, getCurrentInstance, ref } from "vue";
import * as names from "@/store/names";
import { toast } from "@/utils/utils";
import {
  scopeIdsOfCompetences,
  scopesOfEgressProfile,
} from "@/utils/realizationScopes";

const props = defineProps({
  egress_profile_matter: {
    type: Object,
    required: true,
  },
  // Ids de las competencias (no de los vínculos) seleccionadas para la asignatura
  competence_ids: {
    type: Array,
    default: () => [],
  },
  // Competencias del perfil con sus realization_scopes (id = id de la competencia)
  matrix2_competences: {
    type: Array,
    default: () => [],
  },
});
const emit = defineEmits(["close", "saved"]);

const proxy = getCurrentInstance().proxy;
const store = proxy.$store;

const entityNamePlural = computed(() =>
  proxy.$getVisibleNames(
    "mesh.egressprofilerealizationscope",
    true,
    "Ámbitos de realización"
  )
);

const availableScopes = computed(() => {
  const available_ids = scopeIdsOfCompetences(
    props.matrix2_competences,
    props.competence_ids
  );
  return scopesOfEgressProfile(
    store.getters[names.REALIZATION_SCOPES],
    props.egress_profile_matter.egress_profile
  ).filter((x) => available_ids.includes(x.id));
});

const saving = ref(false);
const selected_ids = ref([
  ...(props.egress_profile_matter.realization_scopes || []),
]);
const allSelected = computed(
  () =>
    availableScopes.value.length > 0 &&
    availableScopes.value.every((x) => selected_ids.value.includes(x.id))
);

function selectAll(checked) {
  selected_ids.value = checked ? availableScopes.value.map((x) => x.id) : [];
}

function save() {
  if (saving.value) return;
  saving.value = true;
  // Se descartan los que ya no están disponibles (competencia quitada de la asignatura)
  const available_ids = availableScopes.value.map((x) => x.id);
  store
    .dispatch(names.PATCH_EGRESS_PROFILE_MATTER, {
      egress_profile_matter_id: props.egress_profile_matter.id,
      item: {
        realization_scopes: selected_ids.value.filter((id) =>
          available_ids.includes(id)
        ),
      },
    })
    .then((response) => {
      toast(
        entityNamePlural.value +
          " " +
          proxy.$t("matrix2.matrix2View.updatedPlural")
      );
      emit("saved", response);
      emit("close");
    })
    .finally(() => {
      saving.value = false;
    });
}

store.dispatch(names.FETCH_REALIZATION_SCOPES, {
  egress_profile_id: props.egress_profile_matter.egress_profile,
});
</script>

<style scoped>
.selection-table-container {
  border-radius: var(--kl-table-radius);
  border: 1px solid var(--kl-table-border);
  overflow: hidden;
}
.selection-table th {
  background: var(--kl-table-header-bg) !important;
  color: var(--kl-table-header-text) !important;
  font-weight: var(--kl-fw-bold);
  font-size: var(--kl-table-header-fs);
  text-transform: uppercase;
  letter-spacing: var(--kl-ls-wide);
  padding: 11px 12px !important;
  border: none !important;
  vertical-align: middle;
  text-align: center;
}
.selection-table td {
  color: var(--kl-table-text);
  font-size: var(--kl-table-fs);
  padding: 11px 12px !important;
  border: none !important;
  border-bottom: 1px solid var(--kl-border-soft) !important;
  vertical-align: middle;
  background-color: var(--kl-table-row-bg);
}
.selection-table tbody tr:nth-child(even) > td {
  background-color: var(--kl-table-row-alt-bg);
}
.selection-table tbody tr:hover > td {
  background-color: var(--kl-table-row-hover-bg);
}
.selection-table tbody tr:last-child > td {
  border-bottom: none !important;
}
.selection-column {
  width: 1%;
}
/* custom-checkbox reserva espacio para el texto del label: aquí el checkbox va solo */
.selection-column .custom-control {
  padding-left: 1.5rem;
  min-height: 1.25rem;
}
.scope-name {
  cursor: pointer;
}
.scope-description {
  color: var(--kl-text-muted);
  font-size: var(--kl-fs-small);
  white-space: pre-line;
}
.btn-cancel {
  margin-right: auto;
  margin-left: 1.2em;
}
</style>
